import React, { createContext, useState, useContext, useEffect } from 'react';
import { supabaseClient } from '../config/supabase-client';
import { useAuth } from "./AuthProvider";

interface Blog {
    id: string;
    title: string;
    content: string;
    is_draft: boolean;
    summary: string;
    author: string;
    published_at;
}

interface BlogContextType {
    blogs: Blog[];
    draftBlogs: Blog[];
    addBlog: (blog: Blog) => void;
    updateBlog: (blog: Blog) => void;
    deleteBlog: (id: string) => void;
    setAsDraft: (id: string) => void;
    publishDraft: (id: string) => void;
}

const BlogContext = createContext<BlogContextType | undefined>(undefined);

export const BlogProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [draftBlogs, setDraftBlogs] = useState<Blog[]>([]);
    const { user } = useAuth();

    useEffect(() => {
        if (user) {
            fetchBlogs();
        }
        else {
            fetchBlogs_public();
        }
    }, [user]);

    const fetchBlogs_public = async () => {
        const { data: publicBlogs, error } = await supabaseClient
            .from('blogs')
            .select('*')
            .eq('is_draft', false)
            .order('published_at', { ascending: false });

        if (error) console.error('Error fetching public blogs:', error);
        else setBlogs(publicBlogs || []);
    };

    const fetchBlogs = async () => {
        const { data: publishedBlogs, error: publishedError } = await supabaseClient
            .from('blogs')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_draft', false)
            .order('published_at', { ascending: false });

        const { data: draftBlogsData, error: draftError } = await supabaseClient
            .from('blogs')
            .select('*')
            .eq('user_id', user.id)
            .eq('is_draft', true)
            .order('published_at', { ascending: false });

        if (publishedError) console.error('Error fetching published blogs:', publishedError);
        if (draftError) console.error('Error fetching draft blogs:', draftError);

        setBlogs(publishedBlogs || []);
        setDraftBlogs(draftBlogsData || []);
    };

    const addBlog = async (blog: Blog) => {
        const { data, error } = await supabaseClient
            .from('blogs')
            .insert([{ ...blog, user_id: user.id }]);

        if (error) console.error('Error adding blog:', error);
        else fetchBlogs();
    };

    const updateBlog = async (updatedBlog: Blog) => {
        const { data, error } = await supabaseClient
            .from('blogs')
            .update(updatedBlog)
            .eq('id', updatedBlog.id)
            .eq('user_id', user.id);

        if (error) console.error('Error updating blog:', error);
        else fetchBlogs();
    };

    const deleteBlog = async (id: string) => {
        const { data, error } = await supabaseClient
            .from('blogs')
            .delete()
            .eq('id', id)
            .eq('user_id', user.id);

        if (error) console.error('Error deleting blog:', error);
        else fetchBlogs();
    };

    const setAsDraft = async (id: string) => {
        const { data, error } = await supabaseClient
            .from('blogs')
            .update({ is_draft: true, published_at: null })
            .eq('id', id)
            .eq('user_id', user.id);

        if (error) console.error('Error setting blog as draft:', error);
        else fetchBlogs();
    };

    const publishDraft = async (id: string) => {
        const currentDate = new Date().toISOString();

        const { data, error } = await supabaseClient
            .from('blogs')
            .update({ is_draft: false, published_at: currentDate })
            .eq('id', id)
            .eq('user_id', user.id);

        if (error) console.error('Error publishing draft:', error);
        else fetchBlogs();
    };

    return (
        <BlogContext.Provider value={{ blogs, draftBlogs, addBlog, updateBlog, deleteBlog, setAsDraft, publishDraft }}>
            {children}
        </BlogContext.Provider>
    );
};

export const useBlogContext = () => {
    const context = useContext(BlogContext);
    if (context === undefined) {
        throw new Error('useBlogContext must be used within a BlogProvider');
    }
    return context;
};
